<template>
	<div class="TuitionFee">
		<div class="ControlLabel">
			<span>Annual Tuition Fee
				<a @click="changeCurrencyClicked" class="Currency">{{ userCurrency }}</a>
			</span>
		</div>
		<div class="TuitionFeeSlider">
			<Slider
				v-model="sliderValue"
				:min="minTuitionFee"
				:max="propsMaximumTuitionFee"
				:step="1"
				:merge="-1"
				:tooltips="false"
				:aria="{ 'aria-label': ['Minimum tuition fee', 'Maximum tuition fee'] }"
				@change="handleSliderChange"
			/>
		</div>
		<div class="TuitionFeeValues">
			<div class="ValueInput">
				<label class="ValueLabel">Minimum</label>
				<input
					type="text"
					class="ValueField"
					:value="minValue"
					@input="handleMinFieldInput"
					aria-label="Minimum tuition fee value"
				/>
			</div>
			<div class="ValueInput">
				<label class="ValueLabel">Maximum</label>
				<input
					type="text"
					class="ValueField"
					:value="maxValue"
					@input="handleMaxFieldInput"
					aria-label="Maximum tuition fee value"
				/>
			</div>
		</div>

		<RadioSelectItem
			class="AffordableStudiesOption"
			:item="radioSelectItem"
			:name="'affordable-studies'"
			@selectionChange="handleAffordableStudiesChange"
		/>
	</div>
</template>

<script lang="ts" setup>
import { defineAsyncComponent } from 'vue';
const Slider = defineAsyncComponent(() => import('@vueform/slider'));
import RadioSelectItem from '@components/RadioSelectItem/RadioSelectItem.vue';
import type { ITuitionFeeRange } from '@app/interfaces/dtos/ITuitionFeeRange';
import { useLocalizedContent } from '@composables/useLocalizedContent';
import { useTuitionFee } from '@composables/useTuitionFee';

const props = withDefaults(defineProps<{
	maximumTuitionFee?: number;
}>(), {
	maximumTuitionFee: 50000
})

const emit = defineEmits<{
	(e: 'tuitionFeeRangeChanges', tuitionFeeRange: ITuitionFeeRange): void
}>()

useLocalizedContent();

// Use the business logic composable
const {
	userCurrency,
	sliderValue,
	minValue,
	maxValue,
	minTuitionFee,
	propsMaximumTuitionFee,
	radioSelectItem,
	changeCurrencyClicked,
	handleSliderChange,
	handleMinFieldInput,
	handleMaxFieldInput,
	handleAffordableStudiesChange
} = useTuitionFee({
	maximumTuitionFee: props.maximumTuitionFee,
	onTuitionFeeRangeChange: (range: ITuitionFeeRange) => emit('tuitionFeeRangeChanges', range)
});
</script>

<style src="@vueform/slider/themes/default.css"></style>

<style scoped lang="scss">
.TuitionFee {
	display: flex;
	width: 100%;
	max-width: 20rem;
	flex-direction: column;

	@include PaddingAreas(0, 0.25);

	.ControlLabel {
		display: flex;
		align-items: center;
		@include TextStyle(BodyRegular);

		.Currency {
			color: $Blue-700;
			cursor: pointer;
			@include MarginAreas(0, 0, 0, 0.5);

			&:hover {
				text-decoration: none;
			}
		}
	}

	.TuitionFeeSlider {
		@include PaddingAreas(1.5, 0.5);

		--slider-bg: #{$Grey-300};
		--slider-connect-bg: #{$Orange-500};
		--slider-height: 0.5rem;
		--slider-radius: 1.25rem;
		--slider-handle-bg: #{$White};
		--slider-handle-border: 1px solid #{$Grey-600};
		--slider-handle-width: 1.25rem;
		--slider-handle-height: 1.25rem;
		--slider-handle-radius: 50%;
		--slider-handle-shadow: $BoxShadow;
		--slider-handle-shadow-active: $BoxShadow;
		--slider-handle-ring-width: 0;
	}

	.TuitionFeeValues {
		display: flex;
		width: 90%;
		justify-content: space-between;
		max-width: 17.438rem;
		min-width: 12rem;
		margin: 0 auto;

		@include PaddingAreas(0, 0, 1);

		.ValueInput {
			display: flex;
			flex-direction: column;
			align-items: center;
			width: 5.438rem;
			gap: 0.25rem;

			.ValueLabel {
				color: $Grey-900;
				@include TextStyle(UiSmallRegular);
			}

			.ValueField {
				width: 5.438rem;
				height: 3rem;
				text-align: center;
				color: $Grey-900;
				background: $White;

				border: 1px solid $Grey-600;
				border-radius: 0.25rem;

				&:hover {
					background: $Blue-100;
					border-color: $Blue-700;
				}

				&:focus {
					border: 1px solid $Grey-600;
					outline: 1px solid $Blue-600;
					background: $White;
				}
			}
		}
	}

	.AffordableStudiesOption {
		width: 100%;
		border-top: solid 1px $Grey-200;

		@include PaddingAreas(1, 0, 0, 0);
	}
}
</style>
